/**
 * CR-9095: My suppliers / All suppliers scope for the PO Lines tab.
 *
 * The PO lines endpoint narrows a user holding ERP buyer codes to their own
 * codes unless the request carries `all_suppliers=true` (apply_buyer_scope).
 * PO Headers, Deliveries and Schedule Lines already offer the switch; PO Lines,
 * including its OTIF Review pill, did not, so such a user had no way to see the
 * rest of the site. Same default as those pages (My suppliers), plus
 * `?all_suppliers=true` in the URL opens on All suppliers, which the Classic
 * Procurement Summary links use, and the switch writes its choice back to the
 * URL so a refresh keeps it.
 */
import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';

/** URL param carrying the All suppliers choice (same name the API reads). */
export const ALL_SUPPLIERS_PARAM = 'all_suppliers';

interface UsePOLinesSupplierScopeParams {
  /** Called when the scope changes, to return to page 1 and refetch. */
  onScopeChange: () => void;
}

/**
 * Supplier scope state for PO Lines.
 *
 * @param onScopeChange - re-query trigger, run after the scope changes
 * @returns buyerCodes (empty: no switch), mySuppliersOnly, setMySuppliersOnly,
 *   and the params to merge into the list/count query and the export query
 */
export function usePOLinesSupplierScope({ onScopeChange }: UsePOLinesSupplierScopeParams) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawCodes = user?.buyer_codes;
  const buyerCodes = useMemo(() => rawCodes ?? [], [rawCodes]);
  const [mySuppliersOnly, setMyOnlyState] = useState(
    () => searchParams.get(ALL_SUPPLIERS_PARAM) !== 'true',
  );

  const setMySuppliersOnly = useCallback((myOnly: boolean) => {
    setMyOnlyState(myOnly);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (myOnly) next.delete(ALL_SUPPLIERS_PARAM);
      else next.set(ALL_SUPPLIERS_PARAM, 'true');
      return next;
    }, { replace: true });
    onScopeChange();
  }, [setSearchParams, onScopeChange]);

  /**
   * The scope param for the list and the tab counts: all_suppliers=true only
   * when a user with codes picked All. Users without codes are never scoped.
   */
  const listScopeParams = useMemo((): Record<string, boolean> => (
    buyerCodes.length && !mySuppliersOnly ? { [ALL_SUPPLIERS_PARAM]: true } : {}
  ), [buyerCodes, mySuppliersOnly]);

  /**
   * The scope param for the export. The backend exports the whole company
   * unless it sees all_suppliers=false, so a user with codes states the choice
   * either way and the file matches the grid.
   */
  const exportScopeParams = useMemo((): Record<string, string> => (
    buyerCodes.length ? { [ALL_SUPPLIERS_PARAM]: mySuppliersOnly ? 'false' : 'true' } : {}
  ), [buyerCodes, mySuppliersOnly]);

  return { buyerCodes, mySuppliersOnly, setMySuppliersOnly, listScopeParams, exportScopeParams };
}
